import { getNoteNameFromCoord, get12TETName, getCentDeviation, relativeOctave, D_REF_MIDI } from './keyboard-layouts';
import { cellColors, cellColorsFromHue, pitchCentsToHue, lerpHue } from './note-colors';

export interface VisualizerOptions {
  width: number;
  height: number;
  generator: [number, number];
  d4Hz: number;
  scaleX: number;
  scaleY: number;
  buttonSpacing: number;
  skewFactor: number;
  bFact: number;
}

interface Button {
  x: number;
  y: number;
  coordX: number;
  coordY: number;
  noteName: string;
  isBlackKey: boolean;
  pitchCents: number;
}

const CELL_INSET = 0.93;

export class KeyboardVisualizer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private buttons: Button[] = [];
  private activeNotes = new Set<string>();
  private sourceActiveNotes = new Set<string>();
  private sustainedNotes = new Set<string>();
  private targetNotes = new Set<string>();
  private pressedTargetNotes = new Set<string>();
  private calibratedRange: ReadonlySet<string> | null = null;
  private qwertyLabels: Map<string, string> = new Map();
  private mpeExpression: Map<string, { pressure: number; pitchBend: number }> = new Map();


  private hv1 = { x: 0, y: 0 };
  private hv2 = { x: 0, y: 0 };



  private cssPxPerInch: number = KeyboardVisualizer.detectCssPxPerInch();

  private static detectCssPxPerInch(): number {
    if (typeof window === 'undefined') return 96;
    if (typeof window.matchMedia !== 'function') {
      return Math.max(72, Math.min(300, 96 * (window.devicePixelRatio || 1)));
    }
    let lo = 40, hi = 600;
    while (lo < hi) {
      const mid = Math.floor((lo + hi + 1) / 2);
      if (window.matchMedia(`(min-resolution: ${mid}dpi)`).matches) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    return lo;
  }

  setCssPxPerInch(dpi: number): void {
    this.cssPxPerInch = Math.max(40, Math.min(600, dpi));
    this.generateButtons();
    this.render();
  }

  getCssPxPerInch(): number {
    return this.cssPxPerInch;
  }


  private options: VisualizerOptions = {
    width: 900,
    height: 400,
    generator: [700, 1200],
    d4Hz: 293.66,
    scaleX: 1.0,
    scaleY: 1.0,
    buttonSpacing: 0,
    skewFactor: 0,
    bFact: 0,
  };

  constructor(canvas: HTMLCanvasElement, options?: Partial<VisualizerOptions>) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');
    this.ctx = ctx;

    if (options) {
      this.options = { ...this.options, ...options };
    }

    this.setupCanvas();
    this.generateButtons();
  }



  private setupCanvas(): void {
    const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
    this.canvas.width = this.options.width * dpr;
    this.canvas.height = this.options.height * dpr;
    this.ctx.scale(dpr, dpr);
  }

  setGenerator(generator: [number, number]): void {
    this.options.generator = generator;
    this.generateButtons();
    this.render();
  }

  setD4Hz(hz: number): void {
    this.options.d4Hz = hz;
    this.render();
  }

  getGenerator(): [number, number] {
    return [this.options.generator[0], this.options.generator[1]];
  }

  setScale(scaleX: number, scaleY: number): void {
    this.options.scaleX = Math.max(0.2, Math.min(5.0, scaleX));
    this.options.scaleY = Math.max(0.2, Math.min(5.0, scaleY));
    this.generateButtons();
    this.render();
  }

  setZoom(zoom: number): void {
    this.setScale(zoom, zoom);
  }

  getZoom(): number {
    return (this.options.scaleX + this.options.scaleY) / 2;
  }

  getScale(): { scaleX: number; scaleY: number } {
    return { scaleX: this.options.scaleX, scaleY: this.options.scaleY };
  }

  setButtonSpacing(_spacing: number): void {
    this.render();
  }

  getButtonSpacing(): number {
    return 0;
  }

  getButtonRadius(): number {
    return Math.min(
      Math.abs(this.hv1.x) + Math.abs(this.hv2.x),
      Math.abs(this.hv1.y) + Math.abs(this.hv2.y),
    );
  }

  setSkewFactor(f: number): void {
    this.options.skewFactor = f;
    this.generateButtons();
    this.render();
  }

  getSkewFactor(): number {
    return this.options.skewFactor;
  }

  setBFact(f: number): void {
    this.options.bFact = f;
    this.generateButtons();
    this.render();
  }

  getBFact(): number {
    return this.options.bFact;
  }


  getGridGeometry(): {
    cellHv1: { x: number; y: number };
    cellHv2: { x: number; y: number };
    width: number;
    height: number;
  } {
    const { cellHv1, cellHv2 } = this.getSpacing();
    return {
      cellHv1: { ...cellHv1 },
      cellHv2: { ...cellHv2 },
      width: this.options.width,
      height: this.options.height,
    };
  }

  getGoldenLineY(): number | undefined {
    const { height } = this.options;
    const centerY = height / 2;
    if (centerY > 0 && centerY < height) return centerY;
    return undefined;
  }

  private getSpacing(): {
    genX: number; genY0: number; genX1: number; genY1: number;
    cellHv1: { x: number; y: number }; cellHv2: { x: number; y: number };
  } {
    const { generator, skewFactor, scaleX, scaleY, bFact } = this.options;
    const t = skewFactor;
    const a = generator[0] / generator[1];
    const bSq = Math.max(0.001, (2 / 3) * a - a * a);
    const b = Math.sqrt(bSq);

    const PIANO_KEY_MM   = 23.5;   // standard white piano key width
    const KEY_SIZE_MM    = PIANO_KEY_MM / CELL_INSET;  // ~25.3mm cell → 23.5mm visible
    const MM_PER_INCH    = 25.4;
    const dPy = 2 * Math.round(KEY_SIZE_MM / MM_PER_INCH * this.cssPxPerInch);
    const dGenX  = Math.max(28, b * dPy);        // Striso X spread (min 28px for readability)
    const dGenY0 = a * dPy;                       // fifth Y lean (pitch-proportional)
    const dGenX1 = 0;                              // octave = pure vertical at DCompose
    const dGenY1 = dPy;                            // octave step
    const mCS    = dPy * 0.5;
    const mGenX  = mCS;
    const mGenY0 = mCS;
    const mGenX1 = mCS;
    const mGenY1 = 2 * mCS;
    const genX  = (dGenX  + t * (mGenX  - dGenX))  * scaleX;
    let   genY0 = (dGenY0 + t * (mGenY0 - dGenY0)) * scaleY;
    const genX1 = (dGenX1 + t * (mGenX1 - dGenX1)) * scaleX;
    const genY1 = (dGenY1 + t * (mGenY1 - dGenY1)) * scaleY;
    genY0 = genY0 + bFact * (genY1 / 2 - genY0);
    const cellHv1 = {
      x: (2 * genX - genX1) / 2,
      y: -(2 * genY0 - genY1) / 2,
    };
    const cellHv2 = {
      x: (-genX + genX1) / 2,
      y: (genY0 - genY1) / 2,
    };

    return { genX, genY0, genX1, genY1, cellHv1, cellHv2 };
  }

  private generateButtons(): void {
    this.buttons = [];

    const { width, height } = this.options;
    const { genX, genY0, genX1, genY1, cellHv1, cellHv2 } = this.getSpacing();
    const centerX = width / 2;
    const centerY = height / 2;
    this.hv1 = cellHv1;
    this.hv2 = cellHv2;

    const effectiveScale = Math.min(this.options.scaleX, this.options.scaleY, 1);
    const iRange = Math.min(80, Math.ceil(20 / effectiveScale));
    const jRange = Math.min(48, Math.ceil(12 / effectiveScale));

    for (let i = -iRange; i <= iRange; i++) {
      for (let j = -jRange; j <= jRange; j++) {
        const screenX = centerX + i * genX + j * genX1;
        const screenY = centerY - (i * genY0 + j * genY1);

        const margin = (Math.abs(this.hv1.x) + Math.abs(this.hv2.x) + Math.abs(this.hv1.y) + Math.abs(this.hv2.y)) * 2;
        if (screenX < -margin || screenX > width + margin) continue;
        if (screenY < -margin || screenY > height + margin) continue;

        const pitchCents = i * this.options.generator[0] + j * this.options.generator[1];
        const noteName = getNoteNameFromCoord(i);
        const tetName = get12TETName(i);
        const isBlackKey = tetName.includes('\u266F') || tetName.includes('\u266D');

        this.buttons.push({
          x: screenX,
          y: screenY,
          coordX: i,
          coordY: j,
          noteName,
          isBlackKey,
          pitchCents,
        });
      }
    }

    this.buttons.sort((a, b) => a.y - b.y);
  }

  setActiveNotes(noteIds: string[], sourceIds?: string[]): void {
    this.activeNotes = new Set(noteIds);
    this.sourceActiveNotes = sourceIds ? new Set(sourceIds) : this.activeNotes;
  }

  getCellIdsForMidiNotes(midiNotes: ReadonlySet<number>): string[] {
    const result: string[] = [];
    for (const b of this.buttons) {
      const midi = D_REF_MIDI + b.coordX * 7 + b.coordY * 12;
      if (midiNotes.has(midi)) result.push(`${b.coordX}_${b.coordY}`);
    }
    return result;
  }

  getCellIdsForPitchCents(pitchCentsSet: ReadonlySet<number>, fifth: number): string[] {
    const result: string[] = [];
    for (const b of this.buttons) {
      const pc = Math.round(b.coordX * fifth + b.coordY * 1200);
      if (pitchCentsSet.has(pc)) result.push(`${b.coordX}_${b.coordY}`);
    }
    return result;
  }

  setSustainedNotes(noteIds: string[]): void {
    this.sustainedNotes = new Set(noteIds);
  }

  setTargetNotes(noteIds: string[]): void {
    this.targetNotes = new Set(noteIds);
  }

  setPressedTargetNotes(noteIds: string[]): void {
    this.pressedTargetNotes = new Set(noteIds);
  }

  setCalibratedRange(range: ReadonlySet<string> | null): void {
    this.calibratedRange = range;
  }

  setQwertyLabels(labels: Map<string, string>): void {
    this.qwertyLabels = labels;
  }

  setMPEExpression(expressions: Map<string, { pressure: number; pitchBend: number }>): void {
    const coordPattern = /(-?\d+)_(-?\d+)$/;
    const reKeyed = new Map<string, { pressure: number; pitchBend: number }>();
    for (const [audioId, expr] of expressions) {
      const m = coordPattern.exec(audioId);
      if (m) {
        const coordId = `${m[1]}_${m[2]}`;
        const existing = reKeyed.get(coordId);
        if (!existing || expr.pressure > existing.pressure) {
          reKeyed.set(coordId, expr);
        }
      }
    }
    this.mpeExpression = reKeyed;
  }

  setGameState(_state: string): void {
    this.render();
  }

  setGameProgress(_currentIndex: number, _totalGroups: number, _elapsedMs: number): void {
    this.render();
  }

  render(): void {
    const { width, height } = this.options;

    this.ctx.fillStyle = '#000';
    this.ctx.fillRect(0, 0, width, height);

    for (const button of this.buttons) {
      this.drawCell(button);
    }

    this.drawPitchLines();
  }

  private drawPitchLines(): void {
    const { width, height } = this.options;
    const { genX, genY0, genX1, genY1 } = this.getSpacing();
    const centerX = width / 2;
    const centerY = height / 2;
    const fifth = this.options.generator[0];
    const octave = this.options.generator[1];

    const pitchDx = genX1;
    const pitchDy = -genY1;
    const cofDx = octave * genX - fifth * genX1;
    const cofDy = fifth * genY1 - octave * genY0;
    this.drawAxisLine(centerX, centerY, cofDx, cofDy, 'Circle of Fifths');

    this.drawAxisLine(centerX, centerY, pitchDx, pitchDy, 'Pitch');
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, 3, 0, Math.PI * 2);
    this.ctx.fill();
    this.drawFifthIndexLines(
      centerX, centerY,
      genX, -genY0,
      genX1, -genY1,
    );

  }




  private drawFifthIndexLines(
    cx: number, cy: number,
    genX: number, genY0: number,
    genX1: number, genY1: number,
  ): void {
    const octLen = Math.sqrt(genX1 * genX1 + genY1 * genY1);
    if (octLen < 0.01) return;
    const octNx = genX1 / octLen;
    const octNy = genY1 / octLen;

    const visibleFifths = new Set<number>();
    let maxAbsI = 1;
    for (const b of this.buttons) {
      if (b.coordX !== 0) visibleFifths.add(b.coordX);
      maxAbsI = Math.max(maxAbsI, Math.abs(b.coordX));
    }

    const { width: w, height: h } = this.options;
    const halfLen = Math.sqrt(w * w + h * h) * 2;

    this.ctx.save();
    for (const i of visibleFifths) {
      const ax = cx + i * genX;
      const ay = cy + i * genY0;
      const alpha = Math.max(0.03, 0.12 - (Math.abs(i) / maxAbsI) * 0.06);

      this.ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
      this.ctx.lineWidth = 0.5;
      this.ctx.beginPath();
      this.ctx.moveTo(ax - octNx * halfLen, ay - octNy * halfLen);
      this.ctx.lineTo(ax + octNx * halfLen, ay + octNy * halfLen);
      this.ctx.stroke();
    }

    this.ctx.restore();
  }



  private drawAxisLine(
    cx: number, cy: number,
    dx: number, dy: number,
    label: string
  ): void {
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 0.01) return;

    const { width, height } = this.options;
    const nx = dx / len;
    const ny = dy / len;
    const ext = Math.sqrt(width * width + height * height);

    this.ctx.save();
    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    this.ctx.lineWidth = 1.5;
    this.ctx.beginPath();
    this.ctx.moveTo(cx - nx * ext, cy - ny * ext);
    this.ctx.lineTo(cx + nx * ext, cy + ny * ext);
    this.ctx.stroke();

    const margin = 60;
    const candidates: number[] = [];
    if (nx > 0.001) candidates.push((width - margin - cx) / nx);
    if (nx < -0.001) candidates.push((margin - cx) / nx);
    if (ny > 0.001) candidates.push((height - margin - cy) / ny);
    if (ny < -0.001) candidates.push((margin - cy) / ny);
    const positiveCandidates = candidates.filter(t => t > 0);
    const tMax = positiveCandidates.length > 0
      ? Math.min(...positiveCandidates)
      : ext * 0.4;

    const tipX = cx + nx * tMax;
    const tipY = cy + ny * tMax;

    const arrowLen = 12;
    const arrowHalf = Math.PI / 7;
    const angle = Math.atan2(ny, nx);

    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    this.ctx.beginPath();
    this.ctx.moveTo(tipX, tipY);
    this.ctx.lineTo(
      tipX - arrowLen * Math.cos(angle - arrowHalf),
      tipY - arrowLen * Math.sin(angle - arrowHalf)
    );
    this.ctx.lineTo(
      tipX - arrowLen * Math.cos(angle + arrowHalf),
      tipY - arrowLen * Math.sin(angle + arrowHalf)
    );
    this.ctx.closePath();
    this.ctx.fill();

    const labelDist = 28;
    const lx = tipX - nx * labelDist;
    const ly = tipY - ny * labelDist;
    const perpX = -ny;
    const perpY = nx;
    const perpOff = 14;

    this.ctx.font = 'bold 11px "JetBrains Mono", monospace';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.save();
    this.ctx.translate(lx + perpX * perpOff, ly + perpY * perpOff);
    let textAngle = angle;
    if (textAngle > Math.PI / 2) textAngle -= Math.PI;
    if (textAngle < -Math.PI / 2) textAngle += Math.PI;
    this.ctx.rotate(textAngle);
    this.ctx.fillText(label, 0, 0);
    this.ctx.restore();

    this.ctx.restore();
  }


  private drawCell(button: Button): void {
    const { x, y, coordX, coordY, noteName, isBlackKey } = button;
    const noteId = `${coordX}_${coordY}`;

    const isActive = this.activeNotes.has(noteId);
    const isSourceActive = isActive && this.sourceActiveNotes.has(noteId);
    const isMirrorActive = isActive && !isSourceActive;
    const isUncalibrated = this.calibratedRange !== null && !this.calibratedRange.has(noteId);
    const effectiveActive = isSourceActive || (isMirrorActive && !isUncalibrated);
    const isTarget = this.targetNotes.has(noteId) && !effectiveActive;
    const isTargetPressed = isTarget && this.pressedTargetNotes.has(noteId);
    const isTargetUnpressed = isTarget && !this.pressedTargetNotes.has(noteId);
    const isSustained = this.sustainedNotes.has(noteId) && !effectiveActive;

    const state: 'active' | 'target' | 'target-pressed' | 'sustained' | 'uncalibrated-white' | 'uncalibrated-black' | 'white' | 'black' = effectiveActive ? 'active'
      : isTargetPressed ? 'target-pressed'
      : isTargetUnpressed ? 'target'
      : isSustained ? 'sustained'
      : isUncalibrated ? (isBlackKey ? 'uncalibrated-black' : 'uncalibrated-white')
      : isBlackKey ? 'black'
      : 'white';

    const mpeExpr = this.mpeExpression.get(noteId);
    let cellAlpha = 1.0;
    if (mpeExpr && isActive) {
      cellAlpha = 0.3 + mpeExpr.pressure * 0.7;
    }

    let fillColor: string;
    let textColor: string;
    if (mpeExpr && effectiveActive && Math.abs(mpeExpr.pitchBend) > 0.01) {
      const baseHue = pitchCentsToHue(button.pitchCents);
      const bentCents = button.pitchCents + mpeExpr.pitchBend * 100;
      const bentHue = pitchCentsToHue(bentCents);
      const t = Math.min(1, Math.abs(mpeExpr.pitchBend));
      const blendedHue = lerpHue(baseHue, bentHue, t);
      ({ fill: fillColor, text: textColor } = cellColorsFromHue(blendedHue, state));
    } else {
      ({ fill: fillColor, text: textColor } = cellColors(coordX, state, button.pitchCents));
    }

    const { hv1, hv2 } = this;
    const s = (isActive || isTargetPressed || isTargetUnpressed) ? 1.0 : CELL_INSET;

    const h1x = hv1.x * s, h1y = hv1.y * s;
    const h2x = hv2.x * s, h2y = hv2.y * s;

    const px = (a: number, b: number): number => x + a * h1x + b * h2x;
    const py = (a: number, b: number): number => y + a * h1y + b * h2y;

    this.ctx.beginPath();
    this.ctx.moveTo(px(-1, -1), py(-1, -1));
    this.ctx.lineTo(px( 1, -1), py( 1, -1));
    this.ctx.lineTo(px( 1,  1), py( 1,  1));
    this.ctx.lineTo(px(-1,  1), py(-1,  1));
    this.ctx.closePath();
    this.ctx.fillStyle = fillColor;
    if (isMirrorActive) {
      this.ctx.globalAlpha = 0.8;
      this.ctx.strokeStyle = fillColor;
      this.ctx.lineWidth = 2;
      this.ctx.setLineDash([4, 3]);
      this.ctx.stroke();
      this.ctx.setLineDash([]);
    } else {
      this.ctx.globalAlpha = cellAlpha;
      this.ctx.fill();
    }
    this.ctx.globalAlpha = 1;

    const cellW = (Math.abs(hv1.x) + Math.abs(hv2.x)) * 2;
    const cellH = (Math.abs(hv1.y) + Math.abs(hv2.y)) * 2;
    const cellMin = Math.min(cellW, cellH);
    const fontSize = Math.max(10, Math.min(22, cellMin * 0.38));
    this.ctx.fillStyle = textColor;
    this.ctx.font = `bold ${fontSize}px "JetBrains Mono", monospace`;
    this.ctx.textAlign = 'center';
    const fifth = this.options.generator[0];
    const tetName = get12TETName(coordX);
    const deviation = getCentDeviation(coordX, fifth);
    const hasBracket = noteName !== tetName || Math.abs(deviation) >= 0.5;

    const midi = D_REF_MIDI + coordX * 7 + coordY * 12;
    const octave = relativeOctave(midi, D_REF_MIDI);
    const octStr = octave !== 0 ? String(Math.abs(octave)) : '0';
    const drawOctavePrefix = (noteCenter: number): void => {
      const savedFont = this.ctx.font;
      const savedAlign = this.ctx.textAlign;
      const savedBaseline = this.ctx.textBaseline;
      this.ctx.font = `bold ${fontSize}px "JetBrains Mono", monospace`;
      const nameW = this.ctx.measureText(noteName).width;
      const octFont = Math.max(6, fontSize * 0.45);
      this.ctx.font = `${octFont}px "JetBrains Mono", monospace`;
      this.ctx.textAlign = 'right';
      this.ctx.textBaseline = 'middle';
      const isDRef = coordX === 0 && coordY === 0;
      const octY = octave > 0 ? noteCenter - fontSize * 0.3 : octave < 0 ? noteCenter + fontSize * 0.3 : isDRef ? noteCenter : noteCenter - fontSize * 0.3;
      this.ctx.fillText(octStr, x - nameW / 2 - 1, octY);
      this.ctx.font = savedFont;
      this.ctx.textAlign = savedAlign;
      this.ctx.textBaseline = savedBaseline;
    };

    if (hasBracket && cellMin > 30) {
      this.ctx.textBaseline = 'bottom';
      this.ctx.textAlign = 'center';
      drawOctavePrefix(y - fontSize * 0.45);
      this.ctx.fillText(noteName, x, y);
      const subSize = Math.max(7, fontSize * 0.6);
      this.ctx.font = `${subSize}px "JetBrains Mono", monospace`;
      this.ctx.fillStyle = textColor;
      this.ctx.globalAlpha = 0.6;
      this.ctx.textBaseline = 'top';
      let bracket: string;
      if (Math.abs(deviation) < 0.5) {
        bracket = `(${tetName})`;
      } else if (noteName === tetName) {
        bracket = `(${deviation > 0 ? '+' : ''}${deviation.toFixed(0)}¢)`;
      } else {
        bracket = `(${tetName}${deviation > 0 ? '+' : ''}${deviation.toFixed(0)}¢)`;
      }
      this.ctx.fillText(bracket, x, y + 1);
    } else {
      this.ctx.textBaseline = 'middle';
      this.ctx.textAlign = 'center';
      drawOctavePrefix(y);
      this.ctx.fillText(noteName, x, y);
    }
    this.ctx.globalAlpha = 1;

    const qLabel = this.qwertyLabels.get(noteId);
    if (qLabel) {
      const qSize = Math.max(8, fontSize * 0.55);
      this.ctx.font = `bold ${qSize}px "JetBrains Mono", monospace`;
      const labelW = this.ctx.measureText(qLabel).width;
      const padX = 2;
      const padY = 1;
      const boxW = labelW + padX * 2;
      const boxH = qSize + padY * 2;
      const boxX = x + cellMin * 0.15;
      const boxY = y - cellMin * 0.3;
      this.ctx.globalAlpha = 0.5;
      this.ctx.fillStyle = '#333';
      this.ctx.fillRect(boxX - boxW / 2, boxY - boxH / 2, boxW, boxH);
      this.ctx.strokeStyle = '#888';
      this.ctx.lineWidth = 0.5;
      this.ctx.strokeRect(boxX - boxW / 2, boxY - boxH / 2, boxW, boxH);
      this.ctx.fillStyle = '#fff';
      this.ctx.globalAlpha = 0.8;
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(qLabel, boxX, boxY);
      this.ctx.globalAlpha = 1;
    }
  }

  resize(width: number, height: number): void {
    this.options.width = width;
    this.options.height = height;
    this.setupCanvas();
    this.generateButtons();
    this.render();
  }

  static getNoteId(coordX: number, coordY: number): string {
    return `${coordX}_${coordY}`;
  }

  getButtonAtPoint(screenX: number, screenY: number): { coordX: number; coordY: number; noteId: string } | null {
    if (this.buttons.length === 0) return null;

    const { genX, genY0, genX1, genY1 } = this.getSpacing();
    const { width, height } = this.options;
    const dx = screenX - width / 2;
    const dy = -(screenY - height / 2);
    const det = genX * genY1 - genX1 * genY0;
    const iFloat = det !== 0 ? (dx * genY1 - genX1 * dy) / det : 0;
    const jFloat = det !== 0 ? (genX * dy - dx * genY0) / det : 0;

    let nearest = this.buttons[0];
    let nearestDist = Infinity;

    const iRound = Math.round(iFloat);
    const jRound = Math.round(jFloat);
    for (const button of this.buttons) {
      if (Math.abs(button.coordX - iRound) > 2 || Math.abs(button.coordY - jRound) > 2) continue;
      const bx = screenX - button.x;
      const by = screenY - button.y;
      const dist = bx * bx + by * by;
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = button;
      }
    }

    if (nearestDist === Infinity) {
      for (const button of this.buttons) {
        const bx = screenX - button.x;
        const by = screenY - button.y;
        const dist = bx * bx + by * by;
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = button;
        }
      }
    }

    return {
      coordX: nearest.coordX,
      coordY: nearest.coordY,
      noteId: `${nearest.coordX}_${nearest.coordY}`,
    };
  }

  getButtons(): Button[] {
    return this.buttons;
  }
}
